import { generateUUID } from '#lib/helper/uuid.js';
import { customerStore } from '#lib/stores/customer.js';
import { selectedLocale } from '#lib/stores/i18n.js';
import type { Customer, User } from '#lib/types/generated/index.js';
import { get } from 'svelte/store';

export function userToCustomer(user: User): Customer {
	return {
		id: user.id,
		email: user.email,
		firstName: user.firstName,
		lastName: user.lastName,
		phone: user.phone,
		phoneOptIn: false,
		emailOptIn: false,
		isVip: false,
		totalSpent: 0,
		firstBookingId: null,
		lastBookingId: null,
		totalBookings: 0,
		preferredLocale: '',
		tags: [],
		customFields: {}
	};
}

export function newCustomer(): Customer {
	return {
		id: generateUUID(),
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		phoneOptIn: true,
		emailOptIn: true,
		isVip: false,
		totalSpent: 0,
		firstBookingId: null,
		lastBookingId: null,
		totalBookings: 0,
		preferredLocale: get(selectedLocale),
		tags: [],
		customFields: {}
	};
}

export function searchLocalCustomers(search: string, customers?: Customer[]): Customer[] {
	const source = customers ?? get(customerStore);
	if (!search || !search.trim()) return source;

	const q = search.toLowerCase().trim();
	const cleanPhone = (p: string) =>
		p
			.replace(/[\s\-.]+/g, '')
			.replace(/^(\+|00)(33|32|41|44|34|49|39|351|31|352)(\(0\))?0?/, '0');
	const qPhone = cleanPhone(q);

	return source.filter((customer) => {
		const name = `${customer.firstName} ${customer.lastName}`.toLowerCase();
		const email = customer.email.toLowerCase();
		const phone = cleanPhone(customer.phone).toLowerCase();

		return name.includes(q) || email.includes(q) || phone.includes(qPhone);
	});
}

type CustomerNameFields = {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	phone?: string | null;
};

export function getCustomerDisplayName(
	customer: CustomerNameFields | null | undefined
): string | null {
	if (!customer) return null;

	const firstName = customer.firstName?.trim();
	const formattedFirstName = firstName
		? firstName.charAt(0).toUpperCase() + firstName.slice(1)
		: '';
	const formattedLastName = customer.lastName?.trim().toUpperCase() ?? '';

	const fullName = [formattedFirstName, formattedLastName].filter(Boolean).join(' ').trim();
	if (fullName.length > 0) {
		return fullName;
	}

	const email = customer.email?.trim();
	if (email) {
		return email;
	}

	const phone = customer.phone?.trim();
	if (phone) {
		return phone;
	}

	return null;
}
